import React, { useContext, createContext, useMemo, useEffect } from 'react'
import { useStoredState } from '../hooks/useStoredState'
import { MAP_3D_KEY, MAP_STYLE_DEFAULT, MAP_STYLE_KEY } from '../utils/constants/localStorage.constants'
import {
  MAPBOX_DARK_MAP_STYLE,
  MAPBOX_LIGHT_MAP_STYLE,
  MAPBOX_SATELLITE_MAP_STYLE,
} from '../utils/constants/mapbox.constants'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import { useSessionContext } from './SessionContext'
import { useDarkMode } from './DarkModeContext'

interface UserPreferencesContextType {
  mapStyle: string
  setMapStyle: (style: string) => void
  apply3DMap: boolean
  setApply3DMap: (apply: boolean) => void
  mapStyleUrl: string
}

const UserPreferencesContext = createContext<UserPreferencesContextType>({
  mapStyle: '',
  setMapStyle: () => {},
  apply3DMap: false,
  setApply3DMap: () => {},
  mapStyleUrl: '',
})

export const useUserPreferences = () => useContext(UserPreferencesContext)

interface UserPreferencesContextProviderProps {
  children: React.ReactNode
}

const UserPreferencesContextProvider: React.FC<UserPreferencesContextProviderProps> = ({ children }) => {
  const { user } = useSessionContext()
  const { darkMode } = useDarkMode()
  const advMapsAuth = useFeatureAuth(FEATURES.ADVANCED_MAP_STYLES)

  // vector or sat
  const [mapStyle, setMapStyle] = useStoredState({
    key: MAP_STYLE_KEY,
    defaultValue: MAP_STYLE_DEFAULT,
  })

  const [apply3DMap, setApply3DMap] = useStoredState({
    key: MAP_3D_KEY,
    defaultValue: false,
  })

  useEffect(() => {
    if (user?.id && !advMapsAuth) {
      setMapStyle('vector')
      setApply3DMap(false)
    }
  }, [user, advMapsAuth])

  const mapStyleUrl = useMemo(() => {
    if (mapStyle === 'satellite') {
      return MAPBOX_SATELLITE_MAP_STYLE
    }
    return darkMode ? MAPBOX_DARK_MAP_STYLE : MAPBOX_LIGHT_MAP_STYLE
  }, [mapStyle, darkMode])

  const contextValue = useMemo(
    () => ({
      mapStyle,
      setMapStyle,
      apply3DMap,
      setApply3DMap,
      mapStyleUrl,
    }),
    [mapStyle, apply3DMap, mapStyleUrl]
  )

  return <UserPreferencesContext.Provider value={contextValue}>{children}</UserPreferencesContext.Provider>
}

export default UserPreferencesContextProvider
